import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Checkbox, ConfigProvider, Form, Input, Button, Radio } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { State } from "country-state-city";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import apiClient from "../../../../helpers/apiClient";
import uploadFile from "../../../../utilities/uploadFile";
import { useOnboarding } from "../../../../contexts/OnboardingContext";
import { useUser } from "../../../../contexts/UserContext/UserContext";
import { useToast } from "../../../../contexts/ToastContext";
import { useCategoriesContext } from "../../../../contexts/CategoriesContext";
import customizeRequiredMark from "../../../../components/custom_antd/CustomizeRequiredMark";
import SignaturePad from "../../../../components/Shared/SignaturePad";
import AddressFields from "../AddressFields";

const US_COUNTRY_ISO = "US";
const US_COUNTRY_NAME = "United States";
const FORM_CLASS =
    "onboarding-company-form [&_.ant-form-item]:mb-4 [&_.ant-form-item-label>label]:text-sm [&_.ant-form-item-label>label]:font-medium [&_.ant-form-item-label>label]:text-gray-700 [&_.ant-input]:h-11 [&_.ant-input]:rounded-lg [&_.ant-select-selector]:!min-h-[44px] [&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:!items-center";

const emptyPickupLocation = () => ({
    address: "",
    city: "",
    zip: "",
    state: undefined,
    country: US_COUNTRY_ISO,
});

const normalizeLocation = (location) => ({
    address: location?.address || "",
    city: location?.city || "",
    zip: location?.zip || "",
    state: location?.state || undefined,
    country: US_COUNTRY_ISO,
});

const sameAddress = (a, b) =>
    (a?.address || "").trim().toLowerCase() === (b?.address || "").trim().toLowerCase() &&
    (a?.city || "").trim().toLowerCase() === (b?.city || "").trim().toLowerCase() &&
    (a?.state || "").trim().toLowerCase() === (b?.state || "").trim().toLowerCase();

const toLocationPayload = (location) => ({
    address: location.address,
    city: location.city,
    state: location.state,
    country: US_COUNTRY_NAME,
    isDeleted: false,
});

const CompanyInformation = forwardRef((props, ref) => {
    const { t } = useTranslation("onboarding");
    const [form] = Form.useForm();
    const { updateStepProgress } = useOnboarding();
    const { myCompanyData, fetchCompanyData } = useUser();
    const { locations, fetchLocations, addLocation } = useCategoriesContext();
    const toast = useToast();
    const [loading, setLoading] = useState(false);
    const [signatureFile, setSignatureFile] = useState(null);
    const [isEditingSignature, setIsEditingSignature] = useState(false);
    const pickupPrefillDone = useRef(false);

    const pickupMode = Form.useWatch("pickupMode", form) || "one";
    const useCompanyAddress = Form.useWatch("useCompanyAddress", form);

    useEffect(() => {
        const companyAddress = {
            address: myCompanyData?.address?.address || "",
            city: myCompanyData?.address?.city || "",
            state: myCompanyData?.address?.state || "",
            country: US_COUNTRY_ISO,
            zip: myCompanyData?.address?.zip || "",
        };

        form.setFieldsValue({
            name: myCompanyData?.name || "",
            phone: myCompanyData?.phone || "",
            website: myCompanyData?.website || "",
            address: companyAddress,
        });
    }, [myCompanyData, form]);

    useEffect(() => {
        let cancelled = false;

        const prefillPickup = async () => {
            const saved = await fetchLocations();
            if (cancelled || pickupPrefillDone.current) return;
            pickupPrefillDone.current = true;

            const companyAddress = {
                address: myCompanyData?.address?.address || "",
                city: myCompanyData?.address?.city || "",
                state: myCompanyData?.address?.state || "",
            };
            const savedLocations = (saved || []).map(normalizeLocation);
            const firstLocation = savedLocations[0];
            const matchesCompany = firstLocation && sameAddress(firstLocation, companyAddress);

            let pickupModeValue = "one";
            let useCompanyAddressValue = true;
            let pickupLocations = [emptyPickupLocation(), emptyPickupLocation()];

            if (savedLocations.length >= 2) {
                pickupModeValue = "multiple";
                useCompanyAddressValue = false;
                pickupLocations = savedLocations;
            } else if (savedLocations.length === 1 && !matchesCompany) {
                useCompanyAddressValue = false;
                pickupLocations = [firstLocation, emptyPickupLocation()];
            } else if (savedLocations.length === 1) {
                pickupLocations = [firstLocation, emptyPickupLocation()];
            }

            form.setFieldsValue({
                pickupMode: pickupModeValue,
                useCompanyAddress: useCompanyAddressValue,
                pickupLocations,
            });
        };

        prefillPickup();
        return () => {
            cancelled = true;
        };
    }, [fetchLocations, form, myCompanyData]);

    useEffect(() => {
        const current = form.getFieldValue("pickupLocations") || [];
        if (pickupMode === "multiple" && current.length < 2) {
            form.setFieldsValue({
                pickupLocations: [...current, ...Array.from({ length: 2 - current.length }, emptyPickupLocation)],
            });
        }
        if (pickupMode === "one" && !useCompanyAddress && current.length < 1) {
            form.setFieldsValue({ pickupLocations: [emptyPickupLocation()] });
        }
    }, [pickupMode, useCompanyAddress, form]);

    const getDesiredLocations = (values) => {
        if (values.pickupMode === "one" && values.useCompanyAddress) {
            return [
                {
                    address: values.address?.address,
                    city: values.address?.city,
                    state: values.address?.state,
                    zip: values.address?.zip,
                    country: US_COUNTRY_NAME,
                },
            ];
        }

        const list = (values.pickupLocations || []).filter((loc) => loc?.address && loc?.city && loc?.state);
        return list.map((loc) => ({
            ...loc,
            country: US_COUNTRY_NAME,
        }));
    };

    const saveLocations = async (values) => {
        const desired = getDesiredLocations(values);
        if (desired.length === 0) {
            toast.error({ message: t("steps.companyInfo.atLeastOneLocation") });
            return false;
        }

        const existing = (await fetchLocations()) || locations || [];
        const toCreate = desired.filter((loc) => !existing.some((item) => sameAddress(item, loc)));

        try {
            await Promise.all(
                toCreate.map(async (loc) => {
                    const response = await apiClient.post("/api/location/create", toLocationPayload(loc));
                    if (response?.data?.success && response?.data?.data) {
                        addLocation(response.data.data);
                    }
                }),
            );
            await updateStepProgress("locations", 1, true);
            return true;
        } catch {
            toast.error({ message: t("messages.saveError") });
            return false;
        }
    };

    const onFinish = async (values) => {
        try {
            setLoading(true);

            const payload = {
                name: values.name,
                phone: values.phone,
                website: values.website,
                address: {
                    ...values.address,
                    country: US_COUNTRY_NAME,
                },
            };

            if (signatureFile) {
                try {
                    const signatureUrl = await uploadFile(signatureFile, "Signatures");
                    if (signatureUrl && typeof signatureUrl === "string") {
                        payload.companySignature = signatureUrl;
                    }
                } catch {
                    toast.error({
                        message: t("steps.companyInfo.signatureUploadError"),
                    });
                    return false;
                }
            } else if (myCompanyData?.companySignature) {
                payload.companySignature = myCompanyData.companySignature;
            }

            const response = await apiClient.patch("/api/company/myCompany", payload);

            if (!response?.data?.success) {
                return false;
            }

            const locationsSaved = await saveLocations(values);
            if (!locationsSaved) {
                return false;
            }

            await fetchCompanyData();
            await updateStepProgress("companyInfo", 0, true);
            setSignatureFile(null);
            setIsEditingSignature(false);

            toast.success({
                message: t("messages.saveSuccess"),
            });
            return true;
        } catch {
            toast.error({
                message: t("messages.saveError"),
            });
            return false;
        } finally {
            setLoading(false);
        }
    };

    useImperativeHandle(ref, () => ({
        submit: async () => {
            try {
                const values = await form.validateFields();
                return onFinish(values);
            } catch {
                return false;
            }
        },
    }));

    const countryOptions = [{ value: US_COUNTRY_ISO, label: US_COUNTRY_NAME }];

    const states = State.getStatesOfCountry(US_COUNTRY_ISO).map((state) => ({
        value: state.name,
        label: state.name,
    }));

    return (
        <div>
            <h1 className="text-[28px] font-bold tracking-tight text-gray-900 sm:text-[32px]">
                {t("steps.companyInfo.heading")}
            </h1>
            <p className="mt-1 mb-8 text-sm leading-relaxed text-gray-500 sm:text-base">
                {t("steps.companyInfo.description")}
            </p>

            <ConfigProvider theme={{ token: { colorPrimary: "#5d5dff", borderRadius: 8 } }}>
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                requiredMark={customizeRequiredMark}
                initialValues={{
                    pickupMode: "one",
                    useCompanyAddress: true,
                    pickupLocations: [emptyPickupLocation(), emptyPickupLocation()],
                    address: { country: US_COUNTRY_ISO },
                }}
                className={FORM_CLASS}>
                <Form.Item
                    label={t("steps.companyInfo.companyName")}
                    name="name"
                    rules={[{ required: true, message: t("validation.required") }]}>
                    <Input placeholder={t("steps.companyInfo.companyNamePlaceholder")} size="large" />
                </Form.Item>

                <Form.Item
                    label={t("steps.companyInfo.phoneNumber")}
                    name="phone"
                    rules={[{ required: true, message: t("validation.required") }]}>
                    <PhoneInput
                        country="us"
                        enableSearch
                        inputStyle={{
                            width: "100%",
                            height: "44px",
                            borderRadius: "8px",
                            borderColor: "#d9d9d9",
                        }}
                        containerStyle={{ width: "100%" }}
                    />
                </Form.Item>

                <Form.Item label={t("steps.companyInfo.website")} name="website">
                    <Input placeholder={t("steps.companyInfo.websitePlaceholder")} size="large" />
                </Form.Item>

                <div className="mb-4 mt-2 border-t border-gray-200 pt-5">
                    <p className="mb-4 text-xs font-semibold uppercase tracking-[0.08em] text-gray-400">
                        {t("steps.companyInfo.companyAddressSection")}
                    </p>
                    <AddressFields namePrefix={["address"]} t={t} states={states} countryOptions={countryOptions} />
                </div>

                <div className="mb-6">
                    <p className="mb-3 text-sm font-medium text-gray-700">
                        {t("steps.companyInfo.pickupLocationsQuestion")}
                    </p>
                    <Form.Item name="pickupMode" className="!mb-0">
                        <Radio.Group className="w-full">
                            <div className="flex w-full flex-col gap-3">
                                <Radio value="one">{t("steps.companyInfo.justOne")}</Radio>
                                {pickupMode === "one" ? (
                                    <div
                                        className="ml-6"
                                        onClick={(event) => event.stopPropagation()}
                                        onKeyDown={(event) => event.stopPropagation()}>
                                        <Form.Item name="useCompanyAddress" valuePropName="checked" noStyle>
                                            <Checkbox>{t("steps.companyInfo.useCompanyAddress")}</Checkbox>
                                        </Form.Item>
                                    </div>
                                ) : null}
                                {pickupMode === "one" && !useCompanyAddress ? (
                                    <Form.List name="pickupLocations">
                                        {(fields) =>
                                            fields.slice(0, 1).map((field) => (
                                                <div
                                                    key={field.key}
                                                    className="ml-6 rounded-xl border border-gray-200 bg-white p-4"
                                                    onClick={(event) => event.stopPropagation()}
                                                    onKeyDown={(event) => event.stopPropagation()}>
                                                    <p className="mb-3 text-sm font-semibold text-gray-800">
                                                        {t("steps.companyInfo.pickupLocationN", { number: 1 })}
                                                    </p>
                                                    <AddressFields
                                                        namePrefix={[field.name]}
                                                        t={t}
                                                        states={states}
                                                        countryOptions={countryOptions}
                                                    />
                                                </div>
                                            ))
                                        }
                                    </Form.List>
                                ) : null}
                                <Radio value="multiple">{t("steps.companyInfo.multipleLocations")}</Radio>
                            </div>
                        </Radio.Group>
                    </Form.Item>

                    {pickupMode === "multiple" ? (
                        <Form.List name="pickupLocations">
                            {(fields, { add, remove }) => (
                                <div className="mt-4 flex flex-col gap-4">
                                    {fields.map((field, index) => (
                                        <div key={field.key} className="rounded-xl border border-gray-200 bg-white p-4">
                                            <div className="mb-3 flex items-center justify-between">
                                                <p className="text-sm font-semibold text-gray-800">
                                                    {t("steps.companyInfo.pickupLocationN", { number: index + 1 })}
                                                </p>
                                                {fields.length > 1 ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => remove(field.name)}
                                                        className="inline-flex items-center gap-1 text-sm font-medium text-red-500 hover:text-red-600">
                                                        <X size={14} />
                                                        {t("steps.companyInfo.remove")}
                                                    </button>
                                                ) : null}
                                            </div>
                                            <AddressFields
                                                namePrefix={[field.name]}
                                                t={t}
                                                states={states}
                                                countryOptions={countryOptions}
                                            />
                                        </div>
                                    ))}

                                    <button
                                        type="button"
                                        onClick={() => add(emptyPickupLocation())}
                                        className="inline-flex items-center justify-center gap-2 py-2 text-sm font-medium text-[#5d5dff] hover:text-[#4b4bd9]">
                                        <PlusOutlined />
                                        {t("steps.companyInfo.addAnotherLocation")}
                                    </button>
                                </div>
                            )}
                        </Form.List>
                    ) : null}
                </div>

                <div className="border-t border-gray-200 pt-5">
                    <h4 className="mb-1 text-base font-semibold text-gray-900">
                        {t("steps.companyInfo.companySignature")}
                    </h4>
                    <p className="mb-3 text-sm text-gray-500">{t("steps.companyInfo.companySignatureDescription")}</p>
                    {myCompanyData?.companySignature && !isEditingSignature ? (
                        <div className="flex flex-col gap-3">
                            <div className="inline-block max-w-full rounded-lg border border-gray-200 bg-white p-4">
                                <img
                                    src={myCompanyData.companySignature}
                                    alt=""
                                    className="max-h-24 object-contain"
                                />
                            </div>
                            <div className="flex gap-2">
                                <p className="text-sm text-gray-500">{t("steps.companyInfo.signatureSaved")}</p>
                                <Button type="link" size="small" onClick={() => setIsEditingSignature(true)}>
                                    {t("steps.companyInfo.updateSignature")}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <SignaturePad
                            onSave={(file) => setSignatureFile(file)}
                            onClear={() => setSignatureFile(null)}
                            t={(key) => t(`steps.companyInfo.${key}`)}
                        />
                    )}
                </div>
            </Form>
            </ConfigProvider>
            {loading ? <p className="mt-2 text-sm text-gray-500">{t("common.saving")}</p> : null}
        </div>
    );
});

CompanyInformation.displayName = "CompanyInformation";

export default CompanyInformation;
